import { Component, OnDestroy, OnInit, TemplateRef, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { CallCenterApptTableColumns } from '../core/constants/constants.model';
import { Appointment } from '../core/model/v2/appointment/appointment';
import { AppointmentStatus } from '../core/model/v2/appointment/appointment-status';
import { AppyUser } from '../core/model/v2/AppyUser';
import { AppointmentsService } from '../core/services/v2/appointments.service';
import { UserService } from '../core/services/v2/user.service';
import { WindowLoaderService } from '../shared/components/window-loader/window-loader.service';
import { CallCenterAppointmentDialogComponent } from './call-center-appointment-dialog/call-center-appointment-dialog.component';

@Component({
  selector: 'app-call-center',
  templateUrl: './call-center.component.html',
  styleUrls: ['./call-center.component.scss']
})
export class CallCenterComponent implements OnInit, OnDestroy {
  appointments: Appointment[];
  requestedAppts: Appointment[];
  pendingAppts: Appointment[];
  upcomingAppts: Appointment[];
  user: AppyUser;
  displayedColumns: string[] = CallCenterApptTableColumns;
  filterForm: FormGroup = this.fb.group({
    patientName: [''],
    patientId: [''],
    providerName: [''],
    providerId: [''],
    healthSystemId: [''],
  });
  filterPatientList: any;
  filterPatientFilteredOptions: Observable<any>;
  filterProviderList: any;
  filterProviderFilteredOptions: Observable<any>;
  filterLocationList: any;

  constructor(
    private userService: UserService,
    private appointmentService: AppointmentsService,
    public dialog: MatDialog,
    private loaderService: WindowLoaderService,
    private fb: FormBuilder,
    private router: Router
  ) { }

  ngOnInit() {
    this.userService.getUserDetails().subscribe(
      (res) => {
        this.user = res;
        this.getAppointmentsByPatientId();
      },
      (error) => { console.error(error); });
  }

  getAppointmentsByPatientId() {
    const searchCriteria = {
      createdBy: this.user.id,
      byCallCareCoordinator: true
    };
    this.loaderService.blockWithLoader(this.appointmentService.getAppointmentsBySearchCriteria(searchCriteria)).subscribe(
      (details) => {
        const currentDateTime = new Date();
        const currentDate = moment(new Date()).format("MM/DD/YYYY");
        this.appointments = null;
        this.appointments = details.appointmentDetails;
        if (this.appointments.length > 0) {
          this.appointments = this.appointments.filter(appt => appt.startDateTime ? moment(appt.startDateTime).isSameOrAfter(currentDateTime) : moment(appt.date).isSameOrAfter(currentDate));
          if (this.appointments.length > 0) {
            this.requestedAppts = this.appointments.filter(x => x.status == AppointmentStatus.PATIENT_AVAILABILITY_SET);
            this.pendingAppts = this.appointments.filter(x => x.status == AppointmentStatus.PROVIDER_AVAILABILITY_SET || x.status == AppointmentStatus.PATIENT_SELECTED_APPOINTMENTSLOT);
            this.upcomingAppts = this.appointments.filter(x => x.status == AppointmentStatus.APPOINTMENT_SCHEDULED);
            this.fetchUniqueLists(this.appointments);

          }
        }
      },
      (error) => { console.error(error); }
    );
  }
  fetchUniqueLists(appts: Appointment[]) {
    const uniquePatientIds = appts.map(item => item.patient.id).filter((value, index, self) => self.indexOf(value) === index);
    this.filterPatientList = [];
    for (let i = 0; i < uniquePatientIds.length; i++) {
      this.filterPatientList.push(appts.find(x => x.patient.id == uniquePatientIds[i]).patient);
    }
    const uniqueProviderIds = appts.map(item => item.practitioner.id).filter((value, index, self) => self.indexOf(value) === index);
    this.filterProviderList = [];
    for (let i = 0; i < uniqueProviderIds.length; i++) {
      this.filterProviderList.push(appts.find(x => x.practitioner.id == uniqueProviderIds[i]).practitioner);
    }
    const uniqueLocationIds = appts.map(item => item.healthSystem.id).filter((value, index, self) => self.indexOf(value) === index);
    this.filterLocationList = [];
    for (let i = 0; i < uniqueLocationIds.length; i++) {
      this.filterLocationList.push(appts.find(x => x.healthSystem.id == uniqueLocationIds[i]).healthSystem);
    }

    this.setAutoCompleteForFilter();

  }
  setAutoCompleteForFilter() {
    this.filterPatientFilteredOptions = this.filterForm.controls.patientName.valueChanges.pipe(
      startWith(''),
      map(value => this._filterPatientForSearch(value || '')),
    );
    this.filterProviderFilteredOptions = this.filterForm.controls.providerName.valueChanges.pipe(
      startWith(''),
      map(value => this._filterProviderForSearch(value || '')),
    );
  }
  private _filterPatientForSearch(value: string): any {
    let filterValue = '';
    if (typeof value !== 'object') {
      filterValue = value ? value.toLowerCase() : '';
    }
    return this.filterPatientList.filter(option => option.preferredName ? option.preferredName.toLowerCase().includes(filterValue) : option.firstName.toLowerCase().includes(filterValue) || option.lastName.toLowerCase().includes(filterValue));
  }
  private _filterProviderForSearch(value: string): any {
    let filterValue = '';
    if (typeof value !== 'object') {
      filterValue = value ? value.toLowerCase() : '';
    }
    return this.filterProviderList.filter(option => option.fullName.toLowerCase().includes(filterValue));
  }
  onTabChanged() {
    this.filterForm.reset();
    this.resetFilter();
  }

  openDialog() {
    this.dialog.open(CallCenterAppointmentDialogComponent, {
      width: '800px'
    });
  }
  goToAppointment(appt: Appointment) {
    if (appt) {
      const patientId = appt.patientId;
      const providerId = appt.practitionerId;
      const healthSystemId = appt.healthSystemId;
      const appointmentId = appt.id;
      let dataToPass = {
        patientId,
        providerId,
        healthSystemId,
        appointmentId
      };
      this.router.navigate([
        "/callcenter/appointment",
        dataToPass
      ]);
    }
  }

  setFilterPatient(patient: any) {
    if (patient.preferredName) {
      this.filterForm.controls.patientName.setValue(patient.preferredName);
    }
    else {
      this.filterForm.controls.patientName.setValue(`${patient.firstName} ${patient.lastName}`);
    }
    this.filterForm.controls.patientId.setValue(patient.id);
  }
  setFilterProvider(provider: any) {
    this.filterForm.controls.providerName.setValue(provider.fullName);
    this.filterForm.controls.providerId.setValue(provider.id);
  }

  filterAppointments(section: string) {
    switch (section) {
      case 'Requested': {
        this.filterRequested();
        break;
      }
      case 'Pending': {
        this.filterPending();
        break;
      }
      case 'Upcoming': {
        this.filterUpcoming();
        break;
      }
      default:
    }
  }
  resetFilter() {
    this.filterRequested();
    this.filterPending();
    this.filterUpcoming();
  }
  filterRequested() {
    this.requestedAppts = this.appointments.filter(x => x.status == AppointmentStatus.PATIENT_AVAILABILITY_SET);
    const formValue = this.filterForm.getRawValue();
    if (formValue.patientId && formValue.patientName) {
      this.requestedAppts = this.requestedAppts.filter(x => x.patientId == formValue.patientId);
    }
    if (formValue.providerId && formValue.providerName) {
      this.requestedAppts = this.requestedAppts.filter(x => x.practitionerId == formValue.providerId);
    }
    if (formValue.healthSystemId && formValue.healthSystemId != '0') {
      this.requestedAppts = this.requestedAppts.filter(x => x.healthSystemId == formValue.healthSystemId);
    }
  }
  filterPending() {
    this.pendingAppts = this.appointments.filter(x => x.status == AppointmentStatus.PROVIDER_AVAILABILITY_SET || x.status == AppointmentStatus.PATIENT_SELECTED_APPOINTMENTSLOT);
    const formValue = this.filterForm.getRawValue();
    if (formValue.patientId) {
      this.pendingAppts = this.pendingAppts.filter(x => x.patientId == formValue.patientId);
    }
    if (formValue.providerId) {
      this.pendingAppts = this.pendingAppts.filter(x => x.practitionerId == formValue.providerId);
    }
    if (formValue.healthSystemId) {
      this.pendingAppts = this.pendingAppts.filter(x => x.healthSystemId == formValue.healthSystemId);
    }
  }
  filterUpcoming() {
    this.upcomingAppts = this.appointments.filter(x => x.status == AppointmentStatus.APPOINTMENT_SCHEDULED);
    const formValue = this.filterForm.getRawValue();
    if (formValue.patientId) {
      this.upcomingAppts = this.upcomingAppts.filter(x => x.patientId == formValue.patientId);
    }
    if (formValue.providerId) {
      this.upcomingAppts = this.upcomingAppts.filter(x => x.practitionerId == formValue.providerId);
    }
    if (formValue.healthSystemId) {
      this.upcomingAppts = this.upcomingAppts.filter(x => x.healthSystemId == formValue.healthSystemId);
    }
  }

  ngOnDestroy(): void {

  }
}
